import {
    ColumnApiModule,
    ColumnsToolPanelModule,
    ColumnMenuModule,
    CsvExportModule,
    CustomFilterModule,
    ModuleRegistry,
    RenderApiModule,
    ServerSideRowModelModule,
    SetFilterModule,
    TextFilterModule,
    TooltipModule,
    ValidationModule,
} from 'ag-grid-enterprise';
import type { IRowNode, SelectionChangedEvent, ShouldRowBeSkippedParams } from 'ag-grid-community';
import { RowApiModule } from 'ag-grid-community';
import { getFindingSpecs } from 'Apis/library';
import {
    AgGridBase,
    useAgGridColumnStatePersistence,
    useRefreshServerSideWhen,
    useStableServerSideDatasource,
    buildCsv,
    downloadCsv,
    fetchAllPages,
    visibleColumnDefs,
    type FetchPageResult,
} from '../AgGridBase';
import { findingsOrgFilterFromOrgModel } from 'Core-utils/helpers/orgModelFilters';
import { DEFAULT_ERROR_MSG } from 'Core-utils/constants/constants';
import { useApiObjectData } from 'Hooks/useApiObjectData';
import { useI18n } from 'Hooks/useI18n';
import { useSearchParamsState } from 'Hooks/useSearchParamsState';
import { ContentfulContext } from 'Rdx/contexts/contentfulContext';
import { ToastsContext } from 'Rdx/contexts/ToastsContext';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import { Typography } from 'Components/atoms';
import _ from 'lodash';
import {
    AgFindingsGridProps,
    COLUMN_STATE_STORAGE_KEYS,
    DEFAULT_COLUMN_STATE,
    findingsFilterFieldMap,
    FindingsGridState,
    GRID_CONFIG,
} from './AgFindingsGrid.config';
import { getFindingsColumns } from './getFindingsColumns';
import { useFindingsServerSideDataSource } from './FindingsServerSideDataSource';
import ActionsTray from '../ActionsTray/ActionsTray';
import { EmptyState } from 'Components/molecules/EmptyState';
import { RootState } from 'Rdx/store';
import { AuthRole, ConnectorDto, FindingSpecDto, FindingsInsightsDto, KIND } from '@ampsec/platform-client';
import { useFindingsEngagementActions } from 'Hooks/useFindingsEngagementActions';
import EngageIcon from 'Assets/icons/engagement.svg';
import { resolveSsrmSelectedRows } from 'Core-utils/agGridSelection';
import {
    applyUserNameFilterUrlParams,
    mapUrlParamsToUserNameFilterModel,
    USER_NAME_COLUMN_ID,
} from './userNameColumnFilter';
import './styles.scss';

ModuleRegistry.registerModules([
    ServerSideRowModelModule,
    RowApiModule,
    TextFilterModule,
    TooltipModule,
    SetFilterModule,
    CustomFilterModule,
    ColumnApiModule,
    RenderApiModule,
    ValidationModule,
    CsvExportModule,
    ColumnsToolPanelModule,
    ColumnMenuModule,
]);

/** Map AG-Grid filter/sort state to URL params shape used by FindingsGrid (useSearchParamsState). */
function mapGridStateToUrlParams(gridState: FindingsGridState): Record<string, any> {
    const params: Record<string, any> = {};
    if (Array.isArray(gridState.sortModel) && gridState.sortModel.length > 0) {
        const s = gridState.sortModel[0];
        params.sortColumn = s.colId;
        params.sortOrder = s.sort?.toUpperCase() === 'DESC' ? 'DESC' : 'ASC';
    }
    if (gridState.filterModel && typeof gridState.filterModel === 'object') {
        applyUserNameFilterUrlParams(params, gridState.filterModel[USER_NAME_COLUMN_ID]);

        Object.entries(findingsFilterFieldMap).forEach(([_, { modelKey, filterType }]) => {
            if (modelKey === USER_NAME_COLUMN_ID) {
                return;
            }
            const filterObj = gridState.filterModel[modelKey];
            if (!filterObj) {
                return;
            }
            if (filterType === 'set' && Array.isArray(filterObj.values) && filterObj.values.length > 0) {
                params[modelKey] = filterObj.values;
            }
            if (filterType === 'text' && typeof filterObj.filter === 'string' && filterObj.filter.trim()) {
                params[modelKey] = filterObj.filter.trim();
            }
            if (
                (filterType === 'departmentMultiSelect' || filterType === 'tagMultiSelect') &&
                Array.isArray(filterObj.values) &&
                filterObj.values.length > 0
            ) {
                params[modelKey] = filterObj.values.join(',');
            }
        });
    }
    return params;
}

/** Map URL params (from useSearchParamsState) to AG-Grid filterModel and sortModel. */
function mapUrlParamsToGridState(urlFilter: Record<string, any>): FindingsGridState {
    const sortModel: FindingsGridState['sortModel'] = [];
    if (urlFilter.sortColumn && urlFilter.sortOrder) {
        sortModel.push({
            colId: urlFilter.sortColumn,
            sort: (urlFilter.sortOrder === 'DESC' ? 'desc' : 'asc') as 'asc' | 'desc',
        });
    } else {
        // Preserve legacy Risk Advisor default ordering when URL has no explicit sort.
        sortModel.push({ colId: 'severity', sort: 'asc' });
    }
    const filterModel: Record<string, any> = {};
    const userNameFilterModel = mapUrlParamsToUserNameFilterModel(urlFilter);
    if (userNameFilterModel) {
        filterModel[USER_NAME_COLUMN_ID] = userNameFilterModel;
    }

    Object.entries(findingsFilterFieldMap).forEach(([_, { modelKey, filterType }]) => {
        if (modelKey === USER_NAME_COLUMN_ID) {
            return;
        }
        const v = urlFilter[modelKey];
        if (v === undefined) {
            return;
        }
        if (filterType === 'set') {
            const values = Array.isArray(v) ? v : [v];
            if (values.length > 0) {
                filterModel[modelKey] = { filterType: 'set', values };
            }
        }
        if (filterType === 'text' && typeof v === 'string' && v.trim()) {
            filterModel[modelKey] = { filterType: 'text', type: 'contains', filter: v.trim() };
        }
        if (
            (filterType === 'departmentMultiSelect' || filterType === 'tagMultiSelect') &&
            typeof v === 'string' &&
            v.trim()
        ) {
            const values = v
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean);
            if (values.length > 0) {
                filterModel[modelKey] = { filterType, values };
            }
        }
    });
    return { sortModel, filterModel };
}

const STABLE_EMPTY_FILTER: Record<string, any> = {};
const URL_FILTER_KEYS = [
    'severity',
    'finding_status',
    'finding_category',
    'finding_kind',
    'finding_title',
    'provider_name',
    'user_department',
    'finding_tags',
    'user_name',
    'device_display_value',
    'user_associated',
    'q',
] as const;

const AgFindingsGrid: React.FC<AgFindingsGridProps> = ({
    filter,
    defaultFilter,
    findingCategory,
    findingKind,
    tableBodyHeight,
    enableUpdatingURLWithFiltersAndSorting = true,
    showSubHeader: _showSubHeader = false,
    disableFilters = false,
    selectableRows,
    onSelectionChange,
    title,
    reset,
    showHeaderActions = true,
    fullWidth = false,
    preserveUrlParamKeys,
    showOnlyColumns,
    omitColumns,
    emptyStateDescription = 'No Findings match your filters.',
    showSidebar = true,
    usePagination = false,
    defaultFilterModel,
}) => {
    const gridRef = useRef<any>(null);
    const { filter: urlFilter, isUrlLoading, setSearchParamsState } = useSearchParamsState();
    const defaultFilterHydratedRef = useRef(false);

    const normalizedDefaultFilter = useMemo(() => {
        const nextFilter: Record<string, string | string[]> = {};
        if (!defaultFilter || typeof defaultFilter !== 'object') {
            return nextFilter;
        }
        URL_FILTER_KEYS.forEach((key) => {
            const value = defaultFilter[key];
            if (value === undefined || value === null || value === '') {
                return;
            }
            nextFilter[key] = value;
        });
        return nextFilter;
    }, [defaultFilter]);

    const findingSpecTransformer = useCallback((dataItem: FindingSpecDto[]) => {
        return _.uniq((dataItem ?? []).map((spec) => spec.eventType));
    }, []);
    const { data: findingKindOptionsRaw } = useApiObjectData<FindingSpecDto[], string[]>(
        KIND.FINDING_SPECS,
        getFindingSpecs,
        findingSpecTransformer,
        { limit: 250 },
    );
    const findingKindOptions = Array.isArray(findingKindOptionsRaw) ? findingKindOptionsRaw : [];

    const auth = useSelector((state: RootState) => state.auth);
    const { contentfulData } = useContext(ContentfulContext);
    const { addToast } = useContext(ToastsContext);
    const organization = useSelector((state: RootState) => state.orgModel.org);
    const { pidProviderMap, installedConnectorMap } = useSelector((state: RootState) => state);
    const contentfulProviderMap = contentfulData?.providerNameIntegrationMap;

    const [providerFilterOptions, setProviderFilterOptions] = useState<string[]>([]);
    const [providerFilterOptionToProviderKeyMap, setProviderFilterOptionToProviderKeyMap] = useState<
        Record<string, { displayValue?: string }>
    >({});
    const [loadedCount, setLoadedCount] = useState<number | null>(null);
    const [totalCount, setTotalCount] = useState<number | null>(null);
    const [lastUpdatedAt, setLastUpdatedAt] = useState<string | undefined>();
    const [selectedRows, setSelectedRows] = useState<FindingsInsightsDto[]>([]);
    const lastServerSideStateRef = useRef<SelectionChangedEvent<FindingsInsightsDto>['serverSideState']>(null);
    const [actionsShow, setActionsShow] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const { formatDisplayLabel } = useI18n();
    const engagementProps = useFindingsEngagementActions(selectedRows);

    // Use stable empty object when filter is not provided so filterCriteria (and thus datasource)
    // doesn't get a new reference on every re-render (e.g. on checkbox selection). That would
    // force the grid to reload. Only findingCategory/findingKind/urlFilter changes should
    // produce a new datasource (use-case change).
    const effectiveFilter =
        filter !== undefined && filter !== null && Object.keys(filter).length > 0 ? filter : STABLE_EMPTY_FILTER;
    const filterCriteria = useMemo(
        () => ({
            ...effectiveFilter,
            ...findingsOrgFilterFromOrgModel(organization),
        }),
        [effectiveFilter, organization],
    );

    const urlFilterForDatasource = useMemo(() => {
        const f: Record<string, any> = {};
        URL_FILTER_KEYS.forEach((key) => {
            const v = urlFilter[key];
            if (v !== undefined && v !== null && v !== '') {
                f[key] = v;
            }
        });
        return f;
    }, [urlFilter]);

    const handleRowCountChange = useCallback((_filtered: number, total: number) => {
        setTotalCount(total);
        if (total === 0) {
            gridRef.current?.api?.showNoRowsOverlay();
        } else {
            gridRef.current?.api?.hideOverlay();
        }
    }, []);

    const providerDisplayValueMap = useCallback(
        (selectedValues: string[]) => {
            return selectedValues.map((name) => providerFilterOptionToProviderKeyMap[name]?.displayValue ?? name);
        },
        [providerFilterOptionToProviderKeyMap],
    );

    const { datasource } = useFindingsServerSideDataSource({
        filterCriteria,
        urlFilter: urlFilterForDatasource,
        findingCategory,
        findingKind,
        providerDisplayValueMap: providerFilterOptions.length > 0 ? providerDisplayValueMap : undefined,
        onRowCountChange: handleRowCountChange,
        onLastUpdatedAtChange: setLastUpdatedAt,
    });

    const stableDatasource = useStableServerSideDatasource(datasource);

    useRefreshServerSideWhen(gridRef, organization);

    useEffect(() => {
        if (!enableUpdatingURLWithFiltersAndSorting || isUrlLoading || defaultFilterHydratedRef.current) {
            return;
        }
        const missingDefaults: Record<string, string | string[]> = {};
        let hasMissingDefaults = false;
        Object.entries(normalizedDefaultFilter).forEach(([key, value]) => {
            const existingValue = urlFilter[key];
            const hasExistingValue = Array.isArray(existingValue)
                ? existingValue.length > 0
                : existingValue !== undefined && existingValue !== null && existingValue !== '';
            if (!hasExistingValue) {
                missingDefaults[key] = value;
                hasMissingDefaults = true;
            }
        });
        const hasSortColumn = typeof urlFilter.sortColumn === 'string' && urlFilter.sortColumn.length > 0;
        const hasSortOrder = typeof urlFilter.sortOrder === 'string' && urlFilter.sortOrder.length > 0;
        if (!hasSortColumn || !hasSortOrder) {
            missingDefaults.sortColumn = 'severity';
            missingDefaults.sortOrder = 'ASC';
            hasMissingDefaults = true;
        }
        if (hasMissingDefaults) {
            setSearchParamsState({ ...missingDefaults, ...urlFilter });
        }
        defaultFilterHydratedRef.current = true;
    }, [
        enableUpdatingURLWithFiltersAndSorting,
        isUrlLoading,
        normalizedDefaultFilter,
        setSearchParamsState,
        urlFilter,
    ]);

    useEffect(() => {
        if (!pidProviderMap || !contentfulProviderMap) {
            return;
        }
        const options: string[] = [];
        const map: Record<string, { displayValue?: string }> = {};
        Object.values(installedConnectorMap || {}).forEach(
            (connector: ConnectorDto & { provider?: { displayValue?: string } }) => {
                if (!connector?.pid) {
                    return;
                }
                const provider = pidProviderMap?.[connector.pid];
                const displayValue = provider?.displayValue;
                const uiName = contentfulProviderMap?.[displayValue as string]?.name ?? displayValue;
                options.push(uiName);
                map[uiName] = { displayValue };
            },
        );
        setProviderFilterOptions([...options, 'Custom'].filter(Boolean));
        setProviderFilterOptionToProviderKeyMap(map);
    }, [pidProviderMap, contentfulProviderMap, installedConnectorMap]);

    useEffect(() => {
        if (reset) {
            setSearchParamsState({});
        }
    }, [reset, setSearchParamsState]);

    const providerLogoMap = useMemo(() => {
        const integrations = contentfulData?.integrations;
        if (!integrations) {
            return {};
        }
        return Object.keys(integrations).reduce((acc: Record<string, string>, key: string) => {
            const url = (integrations as any)?.[key]?.logo?.fields?.file?.url;
            if (url) {
                acc[(integrations as any)[key].providerKey] = `https:${url}`;
            }
            return acc;
        }, {});
    }, [contentfulData?.integrations]);

    const agGridColumns = useMemo(
        () =>
            getFindingsColumns({
                formatDisplayLabel,
                disableFilters,
                providerFilterOptions,
                findingKindOptions,
                contentfulProviderMap: contentfulProviderMap as Record<string, { name?: string }>,
                providerLogoMap,
                omitUserColumn: false,
                showOnlyColumns,
                omitColumns,
            }),
        [
            formatDisplayLabel,
            disableFilters,
            providerFilterOptions,
            findingKindOptions,
            contentfulProviderMap,
            providerLogoMap,
            showOnlyColumns,
            omitColumns,
        ],
    );

    const { applyOnGridReady, onColumnEvent } = useAgGridColumnStatePersistence({
        storageKey: COLUMN_STATE_STORAGE_KEYS.server,
        defaultState: DEFAULT_COLUMN_STATE,
        columnDefs: agGridColumns,
    });

    const findingsUrlSync = useMemo(
        () => ({
            enabled: enableUpdatingURLWithFiltersAndSorting,
            adapter: {
                fromUrl: (params: Record<string, string | string[] | undefined>) => mapUrlParamsToGridState(params),
                toUrl: (state: { filterModel: Record<string, unknown>; sortModel: FindingsGridState['sortModel'] }) =>
                    mapGridStateToUrlParams(state as FindingsGridState),
            },
            preserveKeys: preserveUrlParamKeys,
            debounceMs: 200,
            replaceHistory: true,
            getUrlState: () => urlFilter,
            setUrlState: (next: Record<string, string | string[] | undefined>) => setSearchParamsState(next),
            isUrlLoading,
            urlStateRevision: urlFilter,
        }),
        [enableUpdatingURLWithFiltersAndSorting, isUrlLoading, preserveUrlParamKeys, setSearchParamsState, urlFilter],
    );

    const onGridReady = useCallback(
        (params: any) => {
            applyOnGridReady(params.api);
            // When URL sync is disabled (e.g. User Detail page), useAgGridUrlSync never
            // fires, so the default severity-ASC sort and any defaultFilterModel are
            // never applied from the URL. Apply them here on grid ready.
            if (!enableUpdatingURLWithFiltersAndSorting) {
                // Always apply the default sort — this embedded grid has no URL
                // persistence so every page open should start from a known state.
                const columnState: any[] = params.api.getColumnState() ?? [];
                params.api.applyColumnState({
                    state: columnState.map((col: any) =>
                        col.colId === 'severity' ? { ...col, sort: 'desc' } : { ...col, sort: null },
                    ),
                    applyOrder: true,
                });
                if (defaultFilterModel && Object.keys(defaultFilterModel).length > 0) {
                    params.api.setFilterModel(defaultFilterModel);
                }
            }
        },
        [applyOnGridReady, defaultFilterModel, enableUpdatingURLWithFiltersAndSorting],
    );

    const handleColumnEvent = useCallback(
        (params: any) => {
            onColumnEvent(params);
        },
        [onColumnEvent],
    );

    const handleResetColumns = useCallback(() => {
        const gridApi = gridRef.current?.api;
        if (gridApi) {
            gridApi.applyColumnState({ state: DEFAULT_COLUMN_STATE, applyOrder: true });
            localStorage.removeItem(COLUMN_STATE_STORAGE_KEYS.server);
            gridApi.setFilterModel({});
            if (enableUpdatingURLWithFiltersAndSorting) {
                setSearchParamsState({});
            }
        }
    }, [enableUpdatingURLWithFiltersAndSorting, setSearchParamsState]);

    const getMainMenuItems = useCallback(
        (params: any) => {
            const defaultItems = params.defaultItems ?? [];
            return defaultItems.map((item: any) => {
                if (item === 'resetColumns') {
                    return {
                        name: 'Reset Columns',
                        action: () => handleResetColumns(),
                    };
                }
                return item;
            });
        },
        [handleResetColumns],
    );

    const onSelectionChanged = useCallback(
        (params: SelectionChangedEvent<FindingsInsightsDto>) => {
            lastServerSideStateRef.current = params.serverSideState;
            const data = resolveSsrmSelectedRows(params.api, params.serverSideState);
            setSelectedRows(data);
            setActionsShow(data.length > 0);
            onSelectionChange?.(data);
        },
        [onSelectionChange],
    );

    const handleStoreUpdated = useCallback(() => {
        const api = gridRef.current?.api;
        if (!api) {
            return;
        }

        let loaded = 0;
        api.forEachNode((node: IRowNode) => {
            if (!node.stub && node.data !== null && node.data !== undefined) {
                loaded++;
            }
        });
        setLoadedCount(loaded);

        const serverSideState = lastServerSideStateRef.current;
        if (!serverSideState || !('selectAll' in serverSideState) || !serverSideState.selectAll) {
            return;
        }
        const data = resolveSsrmSelectedRows<FindingsInsightsDto>(api, serverSideState);
        setSelectedRows(data);
        setActionsShow(data.length > 0);
        onSelectionChange?.(data);
    }, [onSelectionChange]);

    const handlePostProcessPopup = useCallback((params: any) => {
        if (params?.type !== 'filterMenu') {
            return;
        }
        const popupElement = params?.ePopup as HTMLElement | undefined;
        if (!popupElement) {
            return;
        }
        popupElement.style.minWidth = '34rem';
        popupElement.style.width = 'max-content';
        popupElement.style.maxWidth = '120vw';
    }, []);

    const handleActionsClose = useCallback(() => {
        setSelectedRows([]);
        setActionsShow(false);
        if (gridRef.current?.api) {
            gridRef.current.api.deselectAll();
        }
    }, []);

    const handleClear = useCallback(() => {
        if (gridRef.current?.api) {
            gridRef.current.api.deselectAll();
        }
    }, []);

    const handleRefreshData = useCallback(() => {
        if (gridRef.current?.api) {
            gridRef.current.api.refreshServerSide({ purge: true });
        }
    }, []);

    const canSelectRows =
        selectableRows !== undefined
            ? selectableRows
            : auth?.user?.role === AuthRole.ADMIN || auth?.user?.role === AuthRole.OWNER;

    const handleExportCsv = useCallback(() => {
        const api = gridRef.current?.api;
        if (!api) {
            return;
        }
        if (selectedRows.length > 0) {
            const selectedIds = new Set(selectedRows.map((r) => String(r.id)));
            api.exportDataAsCsv({
                shouldRowBeSkipped: (params: ShouldRowBeSkippedParams<FindingsInsightsDto>) =>
                    !selectedIds.has(String(params.node.data?.id)),
            });
        } else {
            // Ignore re-entrant clicks while a full export is already in flight.
            if (isExporting) {
                return;
            }
            // SSRM only holds the fetched row blocks, so api.exportDataAsCsv() would miss rows.
            // Page through the datasource (reusing its query context) to fetch every filtered row.
            const fetchAll = async (): Promise<FindingsInsightsDto[]> => {
                const sortModel = api.getSortModel?.() ?? [];
                const filterModel = api.getFilterModel?.() ?? {};
                const rows = await fetchAllPages<FindingsInsightsDto>({
                    fetchPage: (offset, limit) =>
                        new Promise<FetchPageResult<FindingsInsightsDto>>((resolve, reject) => {
                            stableDatasource.getRows({
                                request: {
                                    startRow: offset,
                                    // AG Grid's endRow is inclusive (endRow - startRow + 1 rows); this
                                    // repo's datasources treat it as exclusive (endRow - startRow). Using
                                    // endRow = offset + limit - 1 requests at most `limit` rows under either
                                    // convention, so fetchAllPages' offset advancement (capped at the page
                                    // size) never under-shoots and re-fetches a row at a page boundary.
                                    endRow: offset + limit - 1,
                                    sortModel,
                                    filterModel,
                                    rowGroupCols: [],
                                    valueCols: [],
                                    pivotCols: [],
                                    pivotMode: false,
                                    groupKeys: [],
                                },
                                success: (params: any) => {
                                    resolve({ rows: params?.rowData ?? [], total: params?.rowCount });
                                },
                                fail: () => reject(new Error('Failed to fetch findings')),
                                context: undefined,
                                // parentNode/api are part of the SSRM param type but unused by this
                                // datasource; there is no node in this on-demand export context.
                                parentNode: undefined as any,
                                api,
                            });
                        }),
                });
                return rows;
            };

            // ValueGetters may resolve provider/connector display names from params.context, so
            // expose the grid's provider/connector data to the CSV builder (mirrors the grid context).
            const exportContext = {
                contentfulProviderMap,
                providerFilterOptionToProviderKeyMap,
                installedConnectorMap,
                pidProviderMap,
            };

            setIsExporting(true);
            fetchAll()
                .then((rows) => {
                    // Export the columns the user currently sees (order + visibility), not the static defs.
                    const csv = buildCsv(rows, visibleColumnDefs(api, agGridColumns), exportContext);
                    downloadCsv(csv, 'findings.csv');
                })
                .catch(() => {
                    addToast({ severity: 'error', body: DEFAULT_ERROR_MSG });
                })
                .finally(() => {
                    setIsExporting(false);
                });
        }
    }, [
        selectedRows,
        isExporting,
        stableDatasource,
        agGridColumns,
        addToast,
        contentfulProviderMap,
        providerFilterOptionToProviderKeyMap,
        installedConnectorMap,
        pidProviderMap,
    ]);

    return (
        <AgGridBase.Root fullWidth={fullWidth}>
            <AgGridBase.Header
                hidden={!showHeaderActions}
                title={title}
                rowCount={usePagination ? undefined : loadedCount}
                totalCount={totalCount}
                lastUpdatedAt={lastUpdatedAt}
                rightExtras={
                    isExporting ? (
                        <span className="ag-grid-export-status d-flex align-items-center gap-2">
                            <Spinner animation="border" size="sm" />
                            <Typography variant="body10" color="text-low-emphasis">
                                Exporting…
                            </Typography>
                        </span>
                    ) : null
                }
                actionItems={[
                    {
                        label: selectedRows.length > 0 ? `Export ${selectedRows.length} Rows` : 'Export CSV',
                        onClick: handleExportCsv,
                    },
                    { label: 'Reset Columns', onClick: handleResetColumns },
                ]}
            />
            {agGridColumns && agGridColumns.length > 0 && (
                <AgGridBase.Table<FindingsInsightsDto>
                    ref={gridRef}
                    columnDefs={agGridColumns}
                    dataMode="server"
                    serverSideDatasource={stableDatasource}
                    bodyHeight={tableBodyHeight || GRID_CONFIG.defaultHeight}
                    bodyMinHeight={GRID_CONFIG.minHeight}
                    showSidebar={showSidebar}
                    urlSync={findingsUrlSync}
                    pagination={
                        usePagination
                            ? {
                                  pageSize: GRID_CONFIG.paginationPageSize,
                                  pageSizeSelector: [50, 100, 250],
                                  panels: ['pageSummary', 'rowSummary', 'pageSize'],
                              }
                            : undefined
                    }
                    agGridProps={{
                        getRowId: (params) => String(params.data?.id ?? ''),
                        domLayout: 'normal',
                        suppressAggFuncInHeader: true,
                        rowSelection: canSelectRows ? { mode: 'multiRow' } : undefined,
                        onSelectionChanged,
                        onModelUpdated: handleStoreUpdated,
                        onGridReady,
                        onColumnResized: handleColumnEvent,
                        onColumnMoved: handleColumnEvent,
                        onColumnVisible: handleColumnEvent,
                        onColumnPinned: handleColumnEvent,
                        getMainMenuItems,
                        postProcessPopup: handlePostProcessPopup,
                        noRowsOverlayComponent: EmptyState,
                        noRowsOverlayComponentParams: {
                            ImageSrc: EngageIcon,
                            noDescription: false,
                            description: emptyStateDescription,
                        },
                    }}
                />
            )}
            {selectedRows.length > 0 && actionsShow && (
                <ActionsTray
                    showActionsTray={actionsShow}
                    CountOfSelectedItems={selectedRows.length}
                    isFindingsGrid={false}
                    selectedRows={selectedRows}
                    onActionsTrayClose={handleActionsClose}
                    onClear={handleClear}
                    onActionShow={() => setActionsShow(false)}
                    onRefreshData={handleRefreshData}
                    clearSelectedRows={handleClear}
                    {...engagementProps}
                />
            )}
        </AgGridBase.Root>
    );
};

export default AgFindingsGrid;
